import { useSelector } from 'react-redux';
import { useMemo, useState } from 'react';
import { ApplicationState, WithGuideLink } from '@mews-commander/types';
import { useFeatureFlag, useLocalizeText } from '@mews/framework';
import { FeatureFlag, TextKey } from '@mews-commander/enums';
import { container } from '@mews-commander/legacy-app-instance';
import { getUrlParameter, dateTimeToUtc, useCommanderSelector, useCommanderThunkDispatch } from '@mews-commander/utils';
import { getCurrentEnterpriseId, useCurrentEnterpriseId } from '@mews-commander/core';
import { Resource } from '@mews-commander/resources';
import { getCurrentEnterpriseUserPreferredServiceId } from '@mews-commander/legacy-entities/src/selectors/session/enterprise';
import { canCurrentEmployeeUseTimeline } from '@mews-commander/legacy-entities/src/selectors/session/scope';
import { getEnterpriseIdFromServiceId } from '@mews-commander/legacy-entities/src/ducks/services';
import { constructResourcesTree } from '@mews-commander/legacy-entities/src/ducks/resourcesTree';
import { TimelineUrlParams, TimelineUrlParamsKeys } from './schemas/timelineUrlParamsSchema';
import TimelinePage from './pages/TimelinePage';
import { getSelectedDensity, getSelectedTimeUnit } from './selectors/view';
import {
    TimelineFilterSchemaKeys,
    TimelineFilterValues,
    TimelineFilterValuesExtended,
} from './components/TimelineFilters/timelineFilterSchema';
import { TimelineFilterValuesContextProvider } from './contexts/timelineFilterContext';
import { timelineFilterSubmit } from './actions';
import { useTimelineFilterDefaultValuesMergedWithSearchParams } from './components/TimelineFilters/useTimelineFilterDefaultValues';
import { getResourcesIncludingNonCategory } from './reducers';
import { getNewReservationInfoFromSearchParams } from './utils/url';

interface TimelineContainerProps extends WithGuideLink {
    searchParams: TimelineUrlParams;
}

export const checkTimelinePrivilege = (state: ApplicationState) => {
    const enterpriseId = getCurrentEnterpriseId();
    const serviceId = getUrlParameter('serviceId') ?? getCurrentEnterpriseUserPreferredServiceId(state);
    const isCorrectEnterpriseId = enterpriseId === getEnterpriseIdFromServiceId(state, { serviceId });

    const canUseTimeline = canCurrentEmployeeUseTimeline(state);

    return isCorrectEnterpriseId && canUseTimeline;
};

export const TimelineContainer: React.FC<TimelineContainerProps> = ({ guideLink, searchParams }) => {
    const dispatch = useCommanderThunkDispatch();
    const { appLocalizationComponent } = container();

    const timelineFilterDefaultValues = useTimelineFilterDefaultValuesMergedWithSearchParams(searchParams);
    const [filterValues, setFilterValues] = useState<TimelineFilterValues>(timelineFilterDefaultValues);

    const newReservationFromTimelineSlice1Enabled = useFeatureFlag(FeatureFlag.newReservationFromTimelineSlice1);

    const resources = useCommanderSelector(
        (state) =>
            getResourcesIncludingNonCategory(state, {
                serviceId: filterValues[TimelineFilterSchemaKeys.ServiceId],
                filterValues,
            }) as Resource[]
    );

    const resourcesTree = useMemo(() => constructResourcesTree(resources), [resources]);

    const onFilterChanged = (values: TimelineFilterValuesExtended) => {
        dispatch(timelineFilterSubmit(values));
        setFilterValues(values);
    };

    const getUtcDateString = (dateString: string) => {
        try {
            const date = appLocalizationComponent.parseDate(dateString);
            return dateTimeToUtc(date);
        } catch (e) {
            return null;
        }
    };

    const enterpriseId = useCurrentEnterpriseId();

    const density = useSelector(getSelectedDensity);

    const selectedTimeUnit = useCommanderSelector(getSelectedTimeUnit);

    const localizeText = useLocalizeText();

    return (
        <TimelineFilterValuesContextProvider value={filterValues}>
            {/* @ts-expect-error */}
            <TimelinePage
                getUtcDateString={getUtcDateString}
                serviceId={filterValues[TimelineFilterSchemaKeys.ServiceId]}
                enterpriseId={enterpriseId}
                guideLink={guideLink}
                selectedDensity={density}
                selectedDateTime={searchParams[TimelineUrlParamsKeys.DateTime]}
                selectedTimeUnit={selectedTimeUnit}
                selectedReservationId={searchParams[TimelineUrlParamsKeys.Reservation]}
                selectedBlockId={searchParams[TimelineUrlParamsKeys.Block]}
                onFilterChanged={onFilterChanged}
                filterValues={filterValues}
                defaultFilterValues={timelineFilterDefaultValues}
                resources={resourcesTree}
                defaultNewReservationInfo={
                    newReservationFromTimelineSlice1Enabled
                        ? getNewReservationInfoFromSearchParams(searchParams)
                        : undefined
                }
                newReservationPlaceholderLabel={localizeText({ textKey: TextKey.NewReservation })}
            />
        </TimelineFilterValuesContextProvider>
    );
};
